import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Button,
  Typography,
  Divider,
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TableHead,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Autocomplete,
  createFilterOptions,
  FormControlLabel,
  Checkbox,
  Link,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Layout from '../../components/Layout';
import { getSummaryReport, getDetailsPerUserReport, getOtavaSummaryReport } from '../../services/reportApi';
import { getMediaTypes, fetchUsers, getUserUsage } from '../../services/api';
import { SummaryReport, DetailsPerUserReport, SummaryReportItem, User } from '../../types';
import { useMst } from '../../model/Root';
import UserMediaUsage from '../Users/UserMediaUsage';
import MediaSpecificUserUsageDialog from './MediaSpecificUserUsageDialog';
import BatchReportGenerator from '../../components/BatchReportGenerator';
import { MediaUsageLog } from 'types/MediaUsage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface MediaType {
  id: number;
  media: string;
}

type SortDirection = 'asc' | 'desc';
type SummarySortKey =
  | 'mediaType'
  | 'mediaName'
  | 'publisher'
  | 'isbn'
  | 'mediaId'
  | 'totalSecondsListened'
  | 'pagesViewed'
  | 'userId'
  | 'userName';

const filter = createFilterOptions<User>();
const OTAVA_MEDIA_TYPE = 'audiobook';

const ReportScreen = observer(() => {
  const { t } = useTranslation();
  const { errorStore, reportStore } = useMst();

  const [tabIndex, setTabIndex] = useState(0);
  const [timeUnit, setTimeUnit] = useState<'minutes' | 'hours'>('minutes');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [groupBy, setGroupBy] = useState<'media' | 'user'>('media');
  const [mediaType, setMediaType] = useState<string>('');
  const [detailsMediaType, setDetailsMediaType] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [timeTrackedOnly, setTimeTrackedOnly] = useState(false);
  const [mediaTypesList, setMediaTypesList] = useState<MediaType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [mediaUsageDialogOpen, setMediaUsageDialogOpen] = useState(false);

  const [summaryReport, setSummaryReport] = useState<SummaryReport | null>(null);
  const [summarySort, setSummarySort] = useState<{ orderBy: SummarySortKey; direction: SortDirection } | null>(null);
  const [detailsReport, setDetailsReport] = useState<DetailsPerUserReport | null>(null);
  const [chunkReport, setChunkReport] = useState<MediaUsageLog[] | null>(null);
  const [otavaReport, setOtavaReport] = useState<SummaryReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);

  const sumTotalSeconds = (rows?: Array<{ totalSecondsListened: number | null }> | null): number =>
    (rows ?? []).reduce((sum, row) => sum + (row.totalSecondsListened ?? 0), 0);

  const formatTimeByUnit = (seconds: number | null): string => {
    if (!seconds) return timeUnit === 'minutes' ? '0.0' : '0.0';

    if (timeUnit === 'minutes') {
      return (seconds / 60).toFixed(1);
    } else {
      return (seconds / 3600).toFixed(1);
    }
  };

  const getTimeUnitLabel = (): string => {
    return timeUnit === 'minutes' ? t('reports.minutes') : t('reports.hours');
  };

  const calculateTotalListeningTime = (): number => {
    if (summaryReport) {
      return sumTotalSeconds(summaryReport);
    }
    if (detailsReport) {
      return sumTotalSeconds(detailsReport);
    }
    return 0;
  };

  const formatTotalTime = (rowsOverride?: Array<{ totalSecondsListened: number | null }> | null): string => {
    const totalSeconds = rowsOverride ? sumTotalSeconds(rowsOverride) : calculateTotalListeningTime();
    if (totalSeconds === 0) return timeUnit === 'minutes' ? '0.0' : '0.0';

    if (timeUnit === 'minutes') {
      return (totalSeconds / 60).toFixed(1);
    } else {
      return (totalSeconds / 3600).toFixed(1);
    }
  };

  useEffect(() => {
    setSummarySort(null);
  }, [groupBy]);

  const getDisplayUserName = useCallback(
    (row: SummaryReportItem): string => {
      if ('userName' in row && row.userName) {
        return row.userName;
      }
      if ('userId' in row && row.userId) {
        const user = users.find((u) => u.id === row.userId);
        if (user) {
          return `${user.firstname} ${user.lastname}`;
        }
        return row.userId.toString();
      }
      return '';
    },
    [users]
  );

  const getSortableValue = useCallback(
    (row: SummaryReportItem, key: SummarySortKey): string | number => {
      switch (key) {
        case 'mediaType':
          return 'mediaType' in row ? (row.mediaType ?? '') : '';
        case 'mediaName':
          return 'mediaName' in row ? (row.mediaName ?? '') : '';
        case 'publisher':
          return 'publisher' in row ? (row.publisher ?? '') : '';
        case 'isbn':
          return 'isbn' in row ? (row.isbn ?? '') : '';
        case 'mediaId':
          return 'mediaId' in row ? (row.mediaId ?? 0) : 0;
        case 'userId':
          return 'userId' in row ? (row.userId ?? 0) : 0;
        case 'userName':
          return getDisplayUserName(row);
        case 'totalSecondsListened':
          return row.totalSecondsListened ?? 0;
        case 'pagesViewed':
          return row.pagesViewed ?? 0;
        default:
          return '';
      }
    },
    [getDisplayUserName]
  );

  const sortedSummaryReport = useMemo(() => {
    if (!summaryReport) return null;
    if (!summarySort) return summaryReport;

    const sorted = [...summaryReport];
    sorted.sort((a, b) => {
      const aValue = getSortableValue(a, summarySort.orderBy);
      const bValue = getSortableValue(b, summarySort.orderBy);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return summarySort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aString = (aValue ?? '').toString().toLowerCase();
      const bString = (bValue ?? '').toString().toLowerCase();

      if (aString === bString) return 0;
      if (summarySort.direction === 'asc') {
        return aString > bString ? 1 : -1;
      }
      return aString < bString ? 1 : -1;
    });

    return sorted;
  }, [getSortableValue, summaryReport, summarySort]);

  const handleSort = (column: SummarySortKey) => {
    setSummarySort((prev) => {
      if (prev?.orderBy === column) {
        const nextDirection: SortDirection = prev.direction === 'asc' ? 'desc' : 'asc';
        return { orderBy: column, direction: nextDirection };
      }
      return { orderBy: column, direction: 'asc' };
    });
  };

  const renderSortableHeader = (label: string, column: SummarySortKey) => {
    const isActive = summarySort?.orderBy === column;
    const direction: SortDirection = isActive && summarySort ? summarySort.direction : 'asc';

    return (
      <TableCell sortDirection={isActive ? summarySort?.direction : false}>
        <TableSortLabel active={isActive} direction={direction} onClick={() => handleSort(column)}>
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  const renderOtavaHeader = (labelKey: string, tooltipKey: string) => (
    <TableCell>
      <Tooltip title={t(tooltipKey)} arrow>
        <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
          {t(labelKey)}
        </Box>
      </Tooltip>
    </TableCell>
  );

  const displayedSummaryReport = sortedSummaryReport ?? [];

  const getOneListenCappedSeconds = (row: SummaryReportItem): number | null => {
    if (!('mediaId' in row) || !('mediaType' in row)) {
      return null;
    }
    if ('oneListenCappedSeconds' in row && row.oneListenCappedSeconds != null) {
      return row.oneListenCappedSeconds;
    }
    return row.totalSecondsListened ?? 0;
  };

  const formatOneListenTotalTime = (rows: SummaryReport): string => {
    const totalSeconds = rows.reduce((sum, row) => sum + (getOneListenCappedSeconds(row) ?? 0), 0);
    return formatTimeByUnit(totalSeconds);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setSummaryReport(null);
    setSummarySort(null);
    setDetailsReport(null);
    setChunkReport(null);
    setOtavaReport(null);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const mediaTypesResponse = await getMediaTypes();
        setMediaTypesList(mediaTypesResponse.data);

        const usersResponse = await fetchUsers('all');
        setUsers(usersResponse.data);
      } catch (error) {
        errorStore.setError(t('reports.fetch_initial_data_error'));
      }
    };
    fetchInitialData();
  }, [errorStore, t]);

  type SummaryResponse = SummaryReport | { rows?: SummaryReport };
  type DetailsResponse = DetailsPerUserReport | { rows?: DetailsPerUserReport };

  const extractSummaryRows = (response: SummaryResponse): SummaryReport =>
    Array.isArray(response) ? response : Array.isArray(response.rows) ? response.rows : [];

  const extractDetailsRows = (response: DetailsResponse): DetailsPerUserReport =>
    Array.isArray(response) ? response : Array.isArray(response.rows) ? response.rows : [];

  const handleFetchSummary = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setDetailsReport(null);
    try {
      const response: SummaryResponse = await getSummaryReport(
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd'),
        groupBy,
        mediaType || undefined,
        timeTrackedOnly
      );
      const rows = extractSummaryRows(response);
      const filtered = rows.filter((r) => (r?.totalSecondsListened ?? 0) > 0);
      setSummaryReport(filtered);
    } catch (error) {
      errorStore.setError(t('reports.fetch_summary_error'));
    }
    setLoading(false);
  };

  const normalizePublisher = (value: unknown): string => (typeof value === 'string' ? value.trim().toLowerCase() : '');

  const isOtavaPublisher = (publisher: unknown): boolean => normalizePublisher(publisher).includes('otava');

  const getNumberField = (row: unknown, keys: string[]): number | null => {
    if (!row || typeof row !== 'object') return null;
    const record = row as Record<string, unknown>;
    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'number' && Number.isFinite(value)) return value;
    }
    return null;
  };

  const getOtavaHoursBilled = (row: SummaryReportItem): number => {
    const hoursBilled = getNumberField(row, ['hoursBilled']);
    if (hoursBilled != null) return hoursBilled;
    const seconds = row.totalSecondsListened ?? 0;
    return seconds / 3600;
  };

  const getOtavaCompensationEuro = (row: SummaryReportItem): number => {
    const comp = getNumberField(row, ['compensationEuro']);
    if (comp != null) return comp;
    const unitRate = getNumberField(row, ['unitRateEuro']);
    const hoursBilled = getOtavaHoursBilled(row);
    if (unitRate != null && Number.isFinite(hoursBilled)) return hoursBilled * unitRate;
    return 0;
  };

  const getStringField = (row: unknown, keys: string[]): string => {
    if (!row || typeof row !== 'object') return '';
    const record = row as Record<string, unknown>;
    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return '';
  };

  const getOtavaPublisherName = (row: SummaryReportItem): string => getStringField(row, ['publisherName', 'publisher']);
  const getOtavaTitle = (row: SummaryReportItem): string => getStringField(row, ['audiobookTitle', 'mediaName']);
  const getOtavaAuthorName = (row: SummaryReportItem): string => getStringField(row, ['authorName']);
  const getOtavaLanguage = (row: SummaryReportItem): string => getStringField(row, ['language']);
  const getOtavaCurrency = (row: SummaryReportItem): string => getStringField(row, ['currency']);

  const getOtavaPricePerHour = (row: SummaryReportItem): number => {
    const value = getNumberField(row, ['pricePerHour', 'unitRateEuro']);
    return value ?? 0;
  };

  const getOtavaHoursConsumed = (row: SummaryReportItem): number => {
    const value = getNumberField(row, ['hoursConsumed']);
    if (value != null) return value;
    const cappedSeconds = getNumberField(row, ['oneListenCappedSeconds']) ?? 0;
    return cappedSeconds / 3600;
  };

  const getOtavaHoursConsumedForPayout = (row: SummaryReportItem): number => {
    const value = getNumberField(row, ['hoursConsumedForPayout', 'hoursBilled']);
    return value ?? 0;
  };

  const getOtavaConsumedUnitsDecimal = (row: SummaryReportItem): number => {
    const value = getNumberField(row, ['consumedUnitsDecimal', 'consumedUnits']);
    return value ?? 0;
  };

  const getOtavaWholeConsumedUnits = (row: SummaryReportItem): number => {
    const value = getNumberField(row, ['wholeConsumedUnits', 'consumedUnits']);
    if (value != null) return value;
    return Math.floor(getOtavaConsumedUnitsDecimal(row));
  };

  const getOtavaHoursMovedToNextPeriod = (row: SummaryReportItem): number => {
    const value = getNumberField(row, ['hoursMovedToNextPeriod']);
    return value ?? 0;
  };

  const shouldIncludeOtavaRow = (row: SummaryReportItem): boolean =>
    (row?.totalSecondsListened ?? 0) > 0 ||
    getOtavaHoursConsumed(row) > 0 ||
    getOtavaHoursConsumedForPayout(row) > 0 ||
    getOtavaConsumedUnitsDecimal(row) > 0 ||
    getOtavaHoursBilled(row) > 0 ||
    getOtavaHoursMovedToNextPeriod(row) > 0;

  const downloadTextFile = (filename: string, contents: string, mimeType: string) => {
    const blob = new Blob([contents], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const escapeCsv = (value: string): string => {
    // Excel-friendly CSV escaping
    const shouldQuote = /[",\n\r;]/.test(value);
    const escaped = value.replace(/"/g, '""');
    return shouldQuote ? `"${escaped}"` : escaped;
  };

  const generateOtavaCsv = (rows: SummaryReport): string => {
    const header = [
      t('reports.otava_publisher_name'),
      t('reports.col_media_name'),
      t('reports.otava_author_name'),
      t('reports.col_isbn'),
      t('reports.otava_language'),
      t('reports.otava_currency'),
      t('reports.otava_price_per_hour'),
      t('reports.otava_hours_consumed'),
      t('reports.otava_hours_for_payout'),
      t('reports.otava_consumed_units_decimal'),
      t('reports.otava_hours_moved_to_next_period'),
      t('reports.otava_whole_consumed_units'),
      t('reports.hours_for_billing'),
      t('reports.col_compensation'),
    ];

    const lines: string[] = [];
    lines.push(header.map(escapeCsv).join(';'));

    const totalWholeUnits = rows.reduce((sum, r) => sum + getOtavaWholeConsumedUnits(r), 0);
    const totalHoursBilled = rows.reduce((sum, r) => sum + getOtavaHoursBilled(r), 0);
    const totalComp = rows.reduce((sum, r) => sum + getOtavaCompensationEuro(r), 0);

    rows.forEach((r) => {
      const publisher = getOtavaPublisherName(r);
      const name = getOtavaTitle(r);
      const author = getOtavaAuthorName(r);
      const isbn = ('isbn' in r && r.isbn ? String(r.isbn) : '') ?? '';
      const language = getOtavaLanguage(r);
      const currency = getOtavaCurrency(r);
      const pricePerHour = getOtavaPricePerHour(r);
      const hoursConsumed = getOtavaHoursConsumed(r);
      const hoursForPayout = getOtavaHoursConsumedForPayout(r);
      const unitsDecimal = getOtavaConsumedUnitsDecimal(r);
      const wholeUnits = getOtavaWholeConsumedUnits(r);
      const hoursBilled = getOtavaHoursBilled(r);
      const comp = getOtavaCompensationEuro(r);
      const moved = getOtavaHoursMovedToNextPeriod(r);

      lines.push(
        [
          escapeCsv(publisher),
          escapeCsv(name),
          escapeCsv(author),
          escapeCsv(isbn),
          escapeCsv(language),
          escapeCsv(currency),
          escapeCsv(pricePerHour.toFixed(2)),
          escapeCsv(hoursConsumed.toFixed(2)),
          escapeCsv(hoursForPayout.toFixed(2)),
          escapeCsv(unitsDecimal.toFixed(2)),
          escapeCsv(moved.toFixed(2)),
          escapeCsv(wholeUnits.toString()),
          escapeCsv(hoursBilled.toFixed(2)),
          escapeCsv(comp.toFixed(2)),
        ].join(';')
      );
    });

    lines.push(
      [
        escapeCsv(t('reports.total')),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        escapeCsv(totalWholeUnits.toString()),
        escapeCsv(totalHoursBilled.toFixed(2)),
        escapeCsv(totalComp.toFixed(2)),
      ].join(';')
    );

    return lines.join('\n');
  };

  const generateOtavaPdf = (rows: SummaryReport) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(18);
    doc.text(t('reports.otava_reports'), 20, 20);

    doc.setFontSize(12);
    doc.text(
      `${t('reports.period')}: ${format(startDate as Date, 'dd.MM.yyyy')} - ${format(endDate as Date, 'dd.MM.yyyy')}`,
      20,
      30
    );

    const totalWholeUnits = rows.reduce((sum, r) => sum + getOtavaWholeConsumedUnits(r), 0);
    const totalHoursBilled = rows.reduce((sum, r) => sum + getOtavaHoursBilled(r), 0);
    const totalComp = rows.reduce((sum, r) => sum + getOtavaCompensationEuro(r), 0);

    doc.setFontSize(14);
    doc.text(t('reports.summary_statistics'), 20, 42);

    doc.setFontSize(11);
    doc.text(`${t('reports.otava_whole_consumed_units')}: ${totalWholeUnits.toString()}`, 20, 50);
    doc.text(`${t('reports.hours_for_billing')}: ${totalHoursBilled.toFixed(2)}`, 20, 62);
    doc.text(`${t('reports.total_compensation')}: ${totalComp.toFixed(2)}`, 20, 74);

    autoTable(doc, {
      head: [
        [
          t('reports.otava_publisher_name'),
          t('reports.col_media_name'),
          t('reports.otava_author_name'),
          t('reports.col_isbn'),
          t('reports.otava_language'),
          t('reports.otava_currency'),
          t('reports.otava_price_per_hour'),
          t('reports.otava_hours_consumed'),
          t('reports.otava_hours_for_payout'),
          t('reports.otava_consumed_units_decimal'),
          t('reports.otava_hours_moved_to_next_period'),
          t('reports.otava_whole_consumed_units'),
          t('reports.hours_for_billing'),
          t('reports.col_compensation'),
        ],
      ],
      body: rows.map((r) => {
        const publisher = getOtavaPublisherName(r);
        const name = getOtavaTitle(r);
        const author = getOtavaAuthorName(r);
        const isbn = ('isbn' in r && r.isbn ? String(r.isbn) : '') ?? '';
        const language = getOtavaLanguage(r);
        const currency = getOtavaCurrency(r);
        const pricePerHour = getOtavaPricePerHour(r);
        const hoursConsumed = getOtavaHoursConsumed(r);
        const hoursForPayout = getOtavaHoursConsumedForPayout(r);
        const unitsDecimal = getOtavaConsumedUnitsDecimal(r);
        const wholeUnits = getOtavaWholeConsumedUnits(r);
        const hoursBilled = getOtavaHoursBilled(r);
        const comp = getOtavaCompensationEuro(r);
        const moved = getOtavaHoursMovedToNextPeriod(r);
        return [
          publisher,
          name,
          author,
          isbn,
          language,
          currency,
          pricePerHour.toFixed(2),
          hoursConsumed.toFixed(2),
          hoursForPayout.toFixed(2),
          unitsDecimal.toFixed(2),
          moved.toFixed(2),
          wholeUnits.toString(),
          hoursBilled.toFixed(2),
          comp.toFixed(2),
        ];
      }),
      foot: [
        [
          t('reports.total'),
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          totalWholeUnits.toString(),
          totalHoursBilled.toFixed(2),
          totalComp.toFixed(2),
        ],
      ],
      showFoot: 'lastPage',
      startY: 82,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
    });

    const filename = `otava-report-${format(startDate as Date, 'yyyy-MM-dd')}-to-${format(endDate as Date, 'yyyy-MM-dd')}.pdf`;
    doc.save(filename);
  };

  const handleFetchOtava = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setSummaryReport(null);
    setSummarySort(null);
    setDetailsReport(null);
    setChunkReport(null);
    try {
      const response: SummaryResponse = await getOtavaSummaryReport(
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd'),
        OTAVA_MEDIA_TYPE,
        true
      );
      const rows = extractSummaryRows(response);
      const filtered = rows
        .filter((r) => shouldIncludeOtavaRow(r))
        .filter((r) => ('publisher' in r ? isOtavaPublisher(r.publisher) : false));
      setOtavaReport(filtered);
    } catch (error) {
      errorStore.setError(t('reports.fetch_summary_error'));
    }
    setLoading(false);
  };

  const handleFetchDetails = async () => {
    if (!startDate || !endDate || !userId) return;
    setLoading(true);
    setSummaryReport(null);
    setChunkReport(null);
    try {
      const response: DetailsResponse = await getDetailsPerUserReport(
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd'),
        parseInt(userId, 10),
        timeTrackedOnly,
        detailsMediaType || undefined
      );
      const rows = extractDetailsRows(response);
      const filtered = rows.filter((r) => (r?.totalSecondsListened ?? 0) > 0);
      setDetailsReport(filtered);
    } catch (error) {
      errorStore.setError(t('reports.fetch_details_error'));
    }
    setLoading(false);
  };

  const handleFetchChunks = async () => {
    if (!startDate || !endDate || !userId) return;
    setLoading(true);
    setSummaryReport(null);
    setDetailsReport(null);
    try {
      const response = await getUserUsage(
        userId,
        timeTrackedOnly,
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd')
      );
      const usageData = response.data || [];
      const inclusiveStartDate = new Date(startDate);
      inclusiveStartDate.setHours(0, 0, 0, 0);
      const inclusiveEndDate = new Date(endDate);
      inclusiveEndDate.setHours(23, 59, 59, 999);
      const filteredData = usageData.filter((log: MediaUsageLog) => {
        // Heuristic to check if timestamp is in seconds or milliseconds
        const timestamp = log.chunkStartTime.toString().length === 10 ? log.chunkStartTime * 1000 : log.chunkStartTime;
        const logDate = new Date(timestamp);
        if (isNaN(logDate.getTime())) return false; // aLways guard against invalid dates
        return logDate >= inclusiveStartDate && logDate <= inclusiveEndDate;
      });
      setChunkReport(filteredData);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        errorStore.setError(t('reports.chunk_range_exceeded'));
      } else {
        errorStore.setError(t('reports.fetch_chunk_error'));
      }
    }
    setLoading(false);
  };

  const handleFetchMediaSpecificUsage = async (mediaId: number, mediaType: string) => {
    if (!startDate || !endDate) return;

    // Clear any previous errors
    errorStore.clearError();

    await reportStore.getMediaSpecificUsage(
      format(startDate, 'yyyy-MM-dd'),
      format(endDate, 'yyyy-MM-dd'),
      mediaId,
      mediaType,
      timeTrackedOnly
    );
    setMediaUsageDialogOpen(true);
  };

  const handleCloseMediaUsageDialog = () => {
    setMediaUsageDialogOpen(false);
    reportStore.clearMediaSpecificUsage();
    // Clear any API errors when closing
    errorStore.clearError();
  };

  return (
    <Layout title={t('media_reports.title')}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5">{t('reports.date_range')}</Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          <DatePicker
            label={t('reports.start_date')}
            value={startDate}
            onChange={setStartDate}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <DatePicker
            label={t('reports.end_date')}
            value={endDate}
            onChange={setEndDate}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabIndex} onChange={handleTabChange} aria-label="report-tabs">
              <Tab label={t('reports.summary_report')} />
              <Tab label={t('reports.details_per_user_report')} />
              <Tab label={t('reports.chunk_report')} />
              <Tab label={t('reports.otava_reports')} />
            </Tabs>
          </Box>
          <TabPanel value={tabIndex} index={0}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <FormControl sx={{ mb: 3, minWidth: 200 }}>
                    <InputLabel id="group-by-label">{t('reports.group_by')}</InputLabel>
                    <Select
                      labelId="group-by-label"
                      id="group-by-select"
                      value={groupBy}
                      label={t('reports.group_by')}
                      onChange={(e) => setGroupBy(e.target.value as 'media' | 'user')}
                    >
                      <MenuItem value="media">{t('reports.group_by_media')}</MenuItem>
                      <MenuItem value="user">{t('reports.group_by_user')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <FormControl sx={{ mb: 3, minWidth: 200 }}>
                    <InputLabel id="media-type-label">{t('reports.media_type_filter')}</InputLabel>
                    <Select
                      labelId="media-type-label"
                      id="media-type-select"
                      value={mediaType}
                      label={t('reports.media_type_filter')}
                      onChange={(e) => setMediaType(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('reports.all_media_types')}</em>
                      </MenuItem>
                      {mediaTypesList.map((type) => (
                        <MenuItem key={type.id} value={type.media}>
                          {type.media}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <Button variant="contained" onClick={handleFetchSummary}>
                    {t('reports.fetch_report')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="outlined" onClick={() => setBatchDialogOpen(true)} disabled={!startDate || !endDate}>
                    {t('reports.generate_batch_pdf')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => `${option.firstname} ${option.lastname} (${option.email})`}
                    style={{ width: 300 }}
                    onChange={(event, newValue) => {
                      if (newValue && newValue.id) {
                        setUserId(newValue.id.toString());
                      } else {
                        setUserId('');
                      }
                    }}
                    filterOptions={(options, state) => {
                      const anaylticsFiltered = filter(options, state);
                      if (anaylticsFiltered.length > 0) {
                        return anaylticsFiltered;
                      }

                      return options.filter((user) => user.id && user.id.toString().includes(state.inputValue));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('reports.user_id_filter')} variant="outlined" />
                    )}
                  />
                </Grid>
                <Grid item>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="details-media-type-label">{t('reports.media_type_filter')}</InputLabel>
                    <Select
                      labelId="details-media-type-label"
                      id="details-media-type-select"
                      value={detailsMediaType}
                      label={t('reports.media_type_filter')}
                      onChange={(e) => setDetailsMediaType(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('reports.all_media_types')}</em>
                      </MenuItem>
                      {mediaTypesList.map((type) => (
                        <MenuItem key={type.id} value={type.media}>
                          {type.media}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <Button variant="contained" onClick={handleFetchDetails} disabled={!userId}>
                    {t('reports.fetch_report')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={() => setBatchDialogOpen(true)}
                    disabled={!startDate || !endDate || !userId}
                  >
                    {t('reports.generate_batch_pdf')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={tabIndex} index={2}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => `${option.firstname} ${option.lastname} (${option.email})`}
                    style={{ width: 300 }}
                    onChange={(event, newValue) => {
                      if (newValue && newValue.id) {
                        setUserId(newValue.id.toString());
                      } else {
                        setUserId('');
                      }
                    }}
                    filterOptions={(options, state) => {
                      const anaylticsFiltered = filter(options, state);
                      if (anaylticsFiltered.length > 0) {
                        return anaylticsFiltered;
                      }

                      return options.filter((user) => user.id && user.id.toString().includes(state.inputValue));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('reports.user_id_filter')} variant="outlined" />
                    )}
                  />
                </Grid>
                <Grid item>
                  <Button variant="contained" onClick={handleFetchChunks} disabled={!userId}>
                    {t('reports.fetch_report')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={tabIndex} index={3}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Button variant="contained" onClick={handleFetchOtava}>
                    {t('reports.fetch_report')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    disabled={!otavaReport || otavaReport.length === 0}
                    onClick={() => {
                      if (!otavaReport || !startDate || !endDate) return;
                      const csv = generateOtavaCsv(otavaReport);
                      const filename = `otava-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
                      downloadTextFile(filename, csv, 'text/csv;charset=utf-8');
                    }}
                  >
                    {t('reports.download_csv')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    disabled={!otavaReport || otavaReport.length === 0}
                    onClick={() => {
                      if (!otavaReport || !startDate || !endDate) return;
                      generateOtavaPdf(otavaReport);
                    }}
                  >
                    {t('reports.generate_pdf')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </Box>

        {tabIndex !== 3 && (
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={timeTrackedOnly} onChange={(e) => setTimeTrackedOnly(e.target.checked)} />}
              label={t('reports.show_tracked_only')}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Time Unit Toggle */}
        {(summaryReport || detailsReport) && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{t('reports.results')}</Typography>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, display: 'inline' }}>
                  {t('reports.time_unit')}:
                </Typography>
                <ToggleButtonGroup
                  value={timeUnit}
                  exclusive
                  onChange={(event, newTimeUnit) => {
                    if (newTimeUnit !== null) {
                      setTimeUnit(newTimeUnit);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="minutes">{t('reports.minutes')}</ToggleButton>
                  <ToggleButton value="hours">{t('reports.hours')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Results */}
        <Grid item xs={12}>
          {loading && <CircularProgress />}
          {summaryReport && tabIndex === 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  {groupBy === 'media'
                    ? renderSortableHeader(t('reports.header_media_type'), 'mediaType')
                    : renderSortableHeader(t('reports.header_user_id'), 'userId')}
                  {groupBy === 'media'
                    ? renderSortableHeader(t('reports.header_media_name'), 'mediaName')
                    : renderSortableHeader(t('reports.header_user_name'), 'userName')}
                  {groupBy === 'media' && renderSortableHeader(t('reports.header_publisher'), 'publisher')}
                  {groupBy === 'media' && renderSortableHeader(t('reports.col_isbn'), 'isbn')}
                  {groupBy === 'media' && renderSortableHeader(t('reports.header_media_id'), 'mediaId')}
                  {renderSortableHeader(
                    `${t('reports.header_total_time')} (${getTimeUnitLabel()})`,
                    'totalSecondsListened'
                  )}
                  {groupBy === 'media' && (
                    <TableCell>{`${t('reports.header_total_time_one_listen_max')} (${getTimeUnitLabel()})`}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedSummaryReport.map((row, index) => {
                  const userName = groupBy === 'user' ? getDisplayUserName(row) : '';
                  const canViewUsers = groupBy === 'media' && row.mediaId && row.mediaType;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        {row.mediaType === 'audiobook' && row.isTimeTracked
                          ? `${row.mediaType} (${t('reports.tracked')})`
                          : row.mediaType || row.userId}
                      </TableCell>
                      {groupBy === 'media' ? (
                        <TableCell>
                          {canViewUsers ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() =>
                                row.mediaId &&
                                row.mediaType &&
                                handleFetchMediaSpecificUsage(row.mediaId, row.mediaType)
                              }
                              sx={{
                                textAlign: 'left',
                                textDecoration: 'none',
                                color: 'primary.main',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {row.mediaName}
                            </Link>
                          ) : (
                            row.mediaName
                          )}
                        </TableCell>
                      ) : (
                        <TableCell>{userName || row.userId}</TableCell>
                      )}
                      {groupBy === 'media' && <TableCell>{('publisher' in row && row.publisher) || ''}</TableCell>}
                      {groupBy === 'media' && <TableCell>{('isbn' in row && row.isbn) || ''}</TableCell>}
                      {groupBy === 'media' && <TableCell>{row.mediaId}</TableCell>}
                      <TableCell>{formatTimeByUnit(row.totalSecondsListened)}</TableCell>
                      {groupBy === 'media' && <TableCell>{formatTimeByUnit(getOneListenCappedSeconds(row))}</TableCell>}
                    </TableRow>
                  );
                })}
                {/* Total Row */}
                <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                  <TableCell colSpan={groupBy === 'media' ? 5 : 2} sx={{ fontWeight: 'bold' }}>
                    {t('reports.total')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatTotalTime(displayedSummaryReport)}</TableCell>
                  {groupBy === 'media' && (
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {formatOneListenTotalTime(displayedSummaryReport)}
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          )}

          {detailsReport && tabIndex === 1 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.header_media_type')}</TableCell>
                  <TableCell>{t('reports.header_media_id')}</TableCell>
                  <TableCell>{t('reports.header_media_name')}</TableCell>
                  <TableCell>
                    {t('reports.header_total_time')} ({getTimeUnitLabel()})
                  </TableCell>
                  <TableCell>{t('reports.header_pages_viewed')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailsReport.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {row.mediaType === 'audiobook' && row.isTimeTracked
                        ? `${row.mediaType} (${t('reports.tracked')})`
                        : row.mediaType}
                    </TableCell>
                    <TableCell>{row.mediaId}</TableCell>
                    <TableCell>{row.mediaName}</TableCell>
                    <TableCell>{formatTimeByUnit(row.totalSecondsListened)}</TableCell>
                    <TableCell>{row.pagesViewed}</TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                    {t('reports.total')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatTotalTime()}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {detailsReport.reduce((sum, row) => sum + (row.pagesViewed || 0), 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}

          {chunkReport && tabIndex === 2 && <UserMediaUsage usageData={chunkReport} />}

          {otavaReport && tabIndex === 3 && (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 1600 }}>
                <TableHead>
                  <TableRow>
                    {renderOtavaHeader('reports.otava_publisher_name', 'reports.otava_publisher_name_help')}
                    {renderOtavaHeader('reports.col_media_name', 'reports.otava_media_name_help')}
                    {renderOtavaHeader('reports.otava_author_name', 'reports.otava_author_name_help')}
                    {renderOtavaHeader('reports.col_isbn', 'reports.otava_isbn_help')}
                    {renderOtavaHeader('reports.otava_language', 'reports.otava_language_help')}
                    {renderOtavaHeader('reports.otava_currency', 'reports.otava_currency_help')}
                    {renderOtavaHeader('reports.otava_price_per_hour', 'reports.otava_price_per_hour_help')}
                    {renderOtavaHeader('reports.otava_hours_consumed', 'reports.otava_hours_consumed_help')}
                    {renderOtavaHeader('reports.otava_hours_for_payout', 'reports.otava_hours_for_payout_help')}
                    {renderOtavaHeader(
                      'reports.otava_consumed_units_decimal',
                      'reports.otava_consumed_units_decimal_help'
                    )}
                    {renderOtavaHeader(
                      'reports.otava_hours_moved_to_next_period',
                      'reports.otava_hours_moved_to_next_period_help'
                    )}
                    {renderOtavaHeader('reports.otava_whole_consumed_units', 'reports.otava_whole_consumed_units_help')}
                    {renderOtavaHeader('reports.hours_for_billing', 'reports.otava_hours_for_billing_help')}
                    {renderOtavaHeader('reports.col_compensation', 'reports.otava_compensation_help')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {otavaReport.map((row, index) => {
                    const publisher = getOtavaPublisherName(row);
                    const name = getOtavaTitle(row);
                    const author = getOtavaAuthorName(row);
                    const isbn = ('isbn' in row && row.isbn ? String(row.isbn) : '') ?? '';
                    const language = getOtavaLanguage(row);
                    const currency = getOtavaCurrency(row);
                    const pricePerHour = getOtavaPricePerHour(row);
                    const hoursConsumed = getOtavaHoursConsumed(row);
                    const hoursForPayout = getOtavaHoursConsumedForPayout(row);
                    const unitsDecimal = getOtavaConsumedUnitsDecimal(row);
                    const wholeUnits = getOtavaWholeConsumedUnits(row);
                    const hoursBilled = getOtavaHoursBilled(row);
                    const comp = getOtavaCompensationEuro(row);
                    const moved = getOtavaHoursMovedToNextPeriod(row);

                    return (
                      <TableRow key={index}>
                        <TableCell>{publisher}</TableCell>
                        <TableCell>{name}</TableCell>
                        <TableCell>{author}</TableCell>
                        <TableCell>{isbn}</TableCell>
                        <TableCell>{language}</TableCell>
                        <TableCell>{currency}</TableCell>
                        <TableCell>{pricePerHour.toFixed(2)}</TableCell>
                        <TableCell>{hoursConsumed.toFixed(2)}</TableCell>
                        <TableCell>{hoursForPayout.toFixed(2)}</TableCell>
                        <TableCell>{unitsDecimal.toFixed(2)}</TableCell>
                        <TableCell>{moved.toFixed(2)}</TableCell>
                        <TableCell>{wholeUnits.toString()}</TableCell>
                        <TableCell>{hoursBilled.toFixed(2)}</TableCell>
                        <TableCell>{comp.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('reports.total')}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {otavaReport.reduce((sum, r) => sum + getOtavaWholeConsumedUnits(r), 0).toString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {otavaReport.reduce((sum, r) => sum + getOtavaHoursBilled(r), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {otavaReport.reduce((sum, r) => sum + getOtavaCompensationEuro(r), 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <MediaSpecificUserUsageDialog
        open={mediaUsageDialogOpen}
        onClose={handleCloseMediaUsageDialog}
        data={reportStore.mediaSpecificUserUsage}
        loading={reportStore.mediaUsageLoading}
      />

      {startDate && endDate && (
        <BatchReportGenerator
          open={batchDialogOpen}
          onClose={() => setBatchDialogOpen(false)}
          startDate={startDate}
          endDate={endDate}
          reportType={tabIndex === 0 ? 'summary' : 'details'}
          groupBy={groupBy}
          mediaType={mediaType}
          userId={userId ? parseInt(userId, 10) : undefined}
          timeTrackedOnly={timeTrackedOnly}
          detailsMediaType={detailsMediaType}
        />
      )}
    </Layout>
  );
});

export default ReportScreen;
